import { useState, useEffect } from 'react';
import { attachmentService } from '../services/attachmentService';
import { Ticket } from '../types/ticket';

interface AttachmentCounts {
  [ticketId: number]: number;
}

/**
 * Custom hook to fetch attachment counts for tickets
 * @param tickets Array of tickets to fetch attachment counts for
 * @returns Object mapping ticket IDs to attachment counts
 */
export function useTicketAttachments(tickets: Ticket[]) {
  const [attachmentCounts, setAttachmentCounts] = useState<AttachmentCounts>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Skip if no tickets
    if (!tickets || tickets.length === 0) return;

    const fetchAttachmentCounts = async () => {
      setLoading(true);
      setError(null);
      
      const counts: AttachmentCounts = {};
      
      try {
        // Process tickets in batches to avoid too many concurrent requests
        const batchSize = 5;
        for (let i = 0; i < tickets.length; i += batchSize) {
          const batch = tickets.slice(i, i + batchSize);
          
          // Create an array of promises for this batch
          const promises = batch.map(async (ticket) => {
            try {
              const attachments = await attachmentService.getAttachments(ticket.id);
              counts[ticket.id] = attachments.length;
            } catch (err) {
              console.error(`Error fetching attachments for ticket ${ticket.id}:`, err);
              counts[ticket.id] = 0; // Default to 0 on error
            }
          });
          
          // Wait for all promises in this batch to resolve
          await Promise.all(promises);
        }
        
        setAttachmentCounts(counts);
      } catch (err) {
        console.error('Error fetching attachment counts:', err);
        setError('Failed to fetch attachment counts');
      } finally {
        setLoading(false);
      }
    };

    fetchAttachmentCounts();
  }, [tickets]);

  return { attachmentCounts, loading, error };
}
